import { useEffect, useMemo, useState } from "react";
import { todayIso } from "./legalClauses";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Download, Plus, Pencil, X, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { ProjectBar } from "@/components/ProjectBar";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useProject } from "@/lib/workflow/useProject";
import { newId } from "@/lib/workflow/ids";
import { EVENT_COST_CATEGORIES, EVENT_INCOME_CATEGORIES, expenseGross } from "@/lib/workflow/budgetOps";
import { ledgerSummary, migrateLegacyExpenses } from "@/lib/workflow/expenseOps";
import { exportRecords } from "@/lib/workflow/layouts";
import { byMonth, expandMonthly, filterByRange, monthRange, monthsIn, receiptZipName } from "@/lib/workflow/ledgerView";
import { buildListPdf } from "../pdf/packetPages";
import * as XLSX from "xlsx";
import type { Expense, ExpenseKind, IncomeStatus, TaxType } from "@/lib/workflow/types";
import { useEmbedded } from "@/components/EmbedContext";
import { NoticeBox } from "@/components/NoticeBox";
import { DeviceKeepPanel } from "@/components/DeviceKeepPanel";
import { useDeviceKeep } from "@/hooks/useDeviceKeep";
import { keptExpenseSnapshot, validateKeptExpenses, BACKUP_REMINDER_EVERY, type KeptExpenses } from "@/lib/workflow/expenseKeep";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "NZD", "INR", "JPY", "CNY", "SGD", "AED", "CHF", "SEK", "NOK", "DKK", "ZAR", "BRL", "MXN", "KRW", "PHP", "IDR", "THB", "TRY", "PLN"];
const PERSONAL_CATEGORIES = ["Food", "Transport", "Housing", "Utilities", "Entertainment", "Health", "Shopping", "Other"];
const BUSINESS_CATEGORIES = ["Office & Supplies", "Software & Subscriptions", "Travel", "Meals & Entertainment", "Marketing & Ads", "Equipment", "Professional Services", "Rent & Utilities", "Payroll & Contractors", "Taxes & Fees", "Other"];
const BUSINESS_INCOME_CATEGORIES = ["Sales", "Services", "Refunds", "Interest", "Other income"];
const PERSONAL_INCOME_CATEGORIES = ["Salary", "Freelance", "Refunds", "Gifts", "Interest", "Other income"];
const EVENT_CATEGORIES: string[] = [...EVENT_COST_CATEGORIES];
/** Income gets its own list in Event mode so a ticket sale is not filed under Venue. */
const categoriesFor = (set: keyof typeof SETS, k: ExpenseKind): string[] => (k === "income" ? (set === "event" ? [...EVENT_INCOME_CATEGORIES] : set === "personal" ? PERSONAL_INCOME_CATEGORIES : BUSINESS_INCOME_CATEGORIES) : SETS[set]);
const SETS = { business: BUSINESS_CATEGORIES, personal: PERSONAL_CATEGORIES, event: EVENT_CATEGORIES } as const;
const STORAGE_KEY = "freedailypro-expense-tracker";
const BUDGET_STORAGE_KEY = "freedailypro-expense-tracker-budget";

/**
 * Receipt files picked in this tab, by entry id. They are held in memory only: never uploaded, never written to
 * storage, and gone when the tab closes, like the entries themselves. The entry keeps just the file name.
 */
const receiptFiles = new Map<string, File>();
const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;

function ReceiptThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = file.type.startsWith("image/");
  useEffect(() => {
    if (!isImage) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file, isImage]);
  return url ? <img src={url} alt={`Receipt ${file.name}`} className="h-8 w-8 rounded object-cover border shrink-0" data-testid="exp-receipt-thumb" /> : <span className="h-8 w-8 rounded border grid place-items-center shrink-0" data-testid="exp-receipt-thumb"><FileText className="h-4 w-4" aria-label={`Receipt ${file.name}`} /></span>;
}

function loadBudget(): string {
  try {
    return sessionStorage.getItem(BUDGET_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

const TOOL_INFO_CONTENT = {
  what: `Tracks costs and income by category in any currency, with tax, vendor and a running total, without needing a dedicated accounting app. Entries live in your project, so the Event Budget Planner, Profit Margin Calculator, and invoice and receipt tools all see them. Attach a receipt photo or PDF to an entry, repeat a monthly bill, filter by month or date range, and optionally set a budget to see how much is left.`,
  how: `Pick your currency, then add each cost or income with a category, amount and optional tax. Totals and the category breakdown update at once. Download a CSV that other tools can open again, an Excel sheet, a PDF, or a ZIP of the CSV with your receipts, or save the whole project file to keep everything.`,
  tip: `Categorize consistently from the start, and enter the tax rate if you want the total to match what you actually paid. Nothing is uploaded anywhere: entries and receipts are erased when you close the tab, so save the project file or export if you want to keep them.`,
};

export default function ExpenseTracker() {
  const [project, store] = useProject();
  const currency = project.currency;
  const expenses = project.data.expenses;
  const [budget, setBudget] = useState("");
  const [date, setDate] = useState(todayIso());
  // Inside an event app (Profit and Expense Manager) the event headings are the natural start.
  const inEventApp = useEmbedded();
  const [categorySet, setCategorySet] = useState<keyof typeof SETS>(inEventApp ? "event" : "business");
  const [kind, setKind] = useState<ExpenseKind>("cost");
  const [category, setCategory] = useState((inEventApp ? EVENT_CATEGORIES : BUSINESS_CATEGORIES)[0]);
  const [status, setStatus] = useState<IncomeStatus>("paid");
  const [description, setDescription] = useState("");
  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [month, setMonth] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState(todayIso());
  const [receipt, setReceipt] = useState<File | null>(null);
  const [receiptCleared, setReceiptCleared] = useState(false);
  const { logUsageNow } = useLogUsageOnAction();
  /** How many entries existed at the last export or Save project: drives the backup reminder and the close prompt. */
  const [backedUp, setBackedUp] = useState(0);

  const money = useMemo(() => {
    let f: Intl.NumberFormat;
    try { f = new Intl.NumberFormat(project.locale || undefined, { style: "currency", currency, maximumFractionDigits: 2 }); } catch { f = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }); }
    return (n: number) => f.format(n);
  }, [currency, project.locale]);

  // Bring over entries saved by the earlier version of this tool, once.
  useEffect(() => {
    setBudget(loadBudget());
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const m = migrateLegacyExpenses(JSON.parse(raw), () => newId("e"), store.get().currency, todayIso());
        if (m.length) {
          store.upsertRecords("expenses", m);
          toast.success(`Brought over ${m.length} earlier expense${m.length > 1 ? "s" : ""}`);
        }
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* storage blocked or unreadable: start empty */ }
  }, [store]);

  useEffect(() => {
    try { sessionStorage.setItem(BUDGET_STORAGE_KEY, budget); } catch { /* storage blocked */ }
  }, [budget]);

  const visible = useMemo(() => filterByRange(expenses, from, to), [expenses, from, to]);
  const filtered = !!(from || to);
  const months = useMemo(() => monthsIn(expenses), [expenses]);
  const monthRows = useMemo(() => byMonth(visible, currency), [visible, currency]);
  const monthMax = Math.max(0, ...monthRows.map((r) => Math.max(r.cost, r.income)));
  const sum = useMemo(() => ledgerSummary(visible, currency), [visible, currency]);
  const plannedFromProject = useMemo(() => project.data.budgets.filter((b) => b.currency === currency).reduce((s, b) => s + b.planned, 0), [project.data.budgets, currency]);
  const budgetNum = Number(budget) > 0 ? Number(budget) : plannedFromProject;
  const categories = Array.from(new Set([...categoriesFor(categorySet, kind), category, ...expenses.filter((e) => e.kind === kind).map((e) => e.category).filter((c): c is string => !!c)]));

  // Opt-in: keep entries, filters and settings in this browser only. Off by default; receipts are never kept.
  const keepSnapshot = useMemo<KeptExpenses>(() => keptExpenseSnapshot({ entries: expenses, currency, budget, month, from, to, categorySet, backedUp }), [expenses, currency, budget, month, from, to, categorySet, backedUp]);
  const keep = useDeviceKeep<KeptExpenses>("expense-tracker", keepSnapshot, {
    validate: validateKeptExpenses,
    isEmpty: (k) => k.entries.length === 0,
    apply: (k) => {
      const cur = store.get();
      const had = cur.data.expenses.length > 0;
      const have = new Set(cur.data.expenses.map((x) => x.id));
      // Merge, never overwrite: entries already on screen win, saved ones fill in.
      const add = k.entries.filter((x) => !have.has(x.id));
      if (add.length) store.upsertRecords("expenses", add);
      if (!had) store.set((p) => ({ ...p, currency: k.currency }));
      setBudget((b) => b || k.budget); setMonth(k.month); setFrom(k.from); setTo(k.to);
      if (k.categorySet in SETS) setCategorySet(k.categorySet as keyof typeof SETS);
      setBackedUp(k.backedUp);
    },
  });
  const markBackedUp = (n = store.get().data.expenses.length) => setBackedUp(n);
  const sinceBackup = Math.max(0, expenses.length - backedUp);
  useEffect(() => {
    // Closing with entries that exist nowhere else: ask once, so the person can export first.
    if (keep.on || sinceBackup === 0) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [keep.on, sinceBackup]);

  const resetForm = () => {
    setAmountError(null); setDate(todayIso()); setDescription(""); setVendor(""); setAmount(""); setTaxRate(""); setTaxIncluded(false); setEditingId(null);
    setReceipt(null); setReceiptCleared(false); setRepeat(false); setStatus("paid");
  };

  const pickMonth = (key: string) => {
    setMonth(key);
    const r = monthRange(key);
    setFrom(r?.from ?? ""); setTo(r?.to ?? "");
  };
  const pickReceipt = (f: File | undefined) => {
    if (!f) return;
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")) { toast.error("Attach a photo (JPG, PNG, WebP) or a PDF."); return; }
    if (f.size > MAX_RECEIPT_BYTES) { toast.error("That file is over 10 MB. Attach a smaller one."); return; }
    setReceipt(f); setReceiptCleared(false);
  };

  const startEdit = (x: Expense) => {
    setEditingId(x.id); setDate(x.date); setKind(x.kind); setCategory(x.category ?? categoriesFor(categorySet, x.kind)[0]); setDescription(x.description);
    setVendor(x.vendor ?? ""); setAmount(String(x.amount)); setTaxRate(x.taxRate ? String(x.taxRate) : ""); setTaxIncluded(x.taxIncluded); setStatus(x.status === "pledged" ? "pledged" : "paid");
    setReceipt(null); setReceiptCleared(false); setRepeat(false);
  };

  const saveExpense = () => {
    const amt = Number(amount);
    if (!amount.trim()) { setAmountError("Enter an amount."); return; }
    if (!Number.isFinite(amt) || amt <= 0) { setAmountError(amt < 0 ? "Amount cannot be negative. Use the Type menu to switch between cost and income." : "Enter an amount above zero."); return; }
    setAmountError(null);
    const rate = Number(taxRate);
    const hasTax = taxRate.trim() !== "" && Number.isFinite(rate) && rate > 0 && rate < 100;
    const taxType: TaxType = hasTax ? "sales_tax" : "none";
    const old = editingId ? expenses.find((x) => x.id === editingId) : undefined;
    const rec: Expense = {
      ...(old ?? {}), id: old?.id ?? newId("e"), date, kind, category: category || undefined, description: (description.trim() || category || "Entry").slice(0, 300),
      vendor: vendor.trim().slice(0, 300) || undefined, amount: Math.round(amt * 100) / 100, currency,
      taxType: hasTax ? (old && old.taxType !== "none" ? old.taxType : taxType) : "none", taxRate: hasTax ? rate : undefined, taxIncluded: hasTax ? taxIncluded : false,
      status: kind === "income" && status === "pledged" ? "pledged" : undefined,
    };
    const attached = receipt ?? (receiptCleared ? null : receiptFiles.get(rec.id) ?? null);
    if (receipt) rec.receipt = receipt.name.slice(0, 300); else if (receiptCleared) delete rec.receipt;
    // A new entry can repeat monthly up to a date; an edit changes just that one entry.
    if (repeat && !old && !(repeatUntil >= date)) { setAmountError("Repeat until must be on or after the date."); return; }
    const recs = repeat && !old ? expandMonthly(rec, repeatUntil, () => newId("e")) : [rec];
    for (const r of recs) { if (attached) receiptFiles.set(r.id, attached); else receiptFiles.delete(r.id); }
    store.upsertRecords("expenses", recs);
    if (recs.length > 1) toast.success(`Added ${recs.length} entries, one for each month`);
    resetForm();
  };

  const removeExpense = (id: string) => {
    receiptFiles.delete(id);
    store.setRecords("expenses", expenses.filter((x) => x.id !== id));
    if (editingId === id) resetForm();
  };

  const clearAll = () => {
    if (!window.confirm("Delete every expense and income line in this project and your budget figure? This can't be undone.")) return;
    receiptFiles.clear();
    store.setRecords("expenses", []);
    setBudget(""); setMonth(""); setFrom(""); setTo(""); setBackedUp(0);
  };

  const changeCurrency = (next: string) => {
    const prev = currency;
    // A currency choice, not a conversion: entries in the old currency are relabelled.
    store.set((p) => ({ ...p, currency: next, data: { ...p.data, expenses: p.data.expenses.map((x) => (x.currency === prev ? { ...x, currency: next } : x)), budgets: p.data.budgets.map((b) => (b.currency === prev ? { ...b, currency: next } : b)) } }));
  };

  // Entering and editing stays free; every download (CSV, Excel, PDF, ZIP) is one gated use.
  const exportCsv = async () => {
    if (!(await logUsageNow())) return;
    downloadBlob(new Blob([exportRecords("expenses", visible)], { type: "text/csv;charset=utf-8" }), "expenses.csv");
    if (!filtered) markBackedUp();
  };
  const exportXlsx = async () => {
    if (!(await logUsageNow())) return;
    const rows: (string | number)[][] = [["Date", "Type", "Category", "Description", "Vendor or client", "Amount", "Tax %", "Tax included", "Total", "Currency", "Income status", "Receipt"],
      ...visible.map((x) => [x.date, x.kind, x.category ?? "", x.description, x.vendor ?? "", x.amount, x.taxRate ?? "", x.taxIncluded ? "yes" : "no", expenseGross(x), x.currency, x.kind === "income" ? x.status ?? "paid" : "", x.receipt ?? ""])];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Entries");
    const sm: (string | number)[][] = [["Month", "Costs", "Income", "Net"], ...monthRows.map((r) => [r.month, r.cost, r.income, Math.round((r.income - r.cost) * 100) / 100])];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sm), "By month");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "expenses.xlsx");
    if (!filtered) markBackedUp();
  };
  const exportPdf = async () => {
    if (!(await logUsageNow())) return;
    try {
      const lines = [`Costs ${money(sum.cost)}, income ${money(sum.income)}, net ${money(sum.net)}${filtered ? ` (${from || "start"} to ${to || "today"})` : ""}`, "",
        ...visible.map((x) => `${x.date}  ${x.kind === "income" ? "+" : "-"}${x.currency === currency ? money(expenseGross(x)) : `${x.amount} ${x.currency}`}  ${x.category ?? ""}  ${x.description}${x.vendor ? ` (${x.vendor})` : ""}`)];
      const bytes = await buildListPdf("Expense log", lines, project.paper === "a4" ? "a4" : "letter");
      downloadBlob(new Blob([new Uint8Array(bytes)], { type: "application/pdf" }), "expenses.pdf");
      if (!filtered) markBackedUp();
    } catch (err) { console.error("Expense PDF failed", err); toast.error("Couldn't build the PDF."); }
  };
  const receiptCount = visible.filter((x) => receiptFiles.has(x.id)).length;
  const exportZip = async () => {
    if (!(await logUsageNow())) return;
    try {
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      const taken = new Set<string>(), nameOf = new Map<File, string>();
      const withPaths = visible.map((x) => {
        const f = receiptFiles.get(x.id);
        if (!f) return x;
        if (!nameOf.has(f)) { nameOf.set(f, receiptZipName(x.date, f.name, taken)); zip.file(nameOf.get(f)!, f); }
        return { ...x, receipt: nameOf.get(f) };
      });
      zip.file("expenses.csv", exportRecords("expenses", withPaths));
      downloadBlob(await zip.generateAsync({ type: "blob" }), "expenses-with-receipts.zip");
      if (!filtered) markBackedUp();
    } catch (err) { console.error("Expense ZIP failed", err); toast.error("Couldn't build the ZIP."); }
  };

  const net = sum.net;
  return (
    <ToolContainer>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-end gap-3 justify-center">
          <div><Label className="text-xs">Currency</Label>
            <select className="border rounded px-2 py-2 bg-background text-sm block" aria-label="Currency" value={currency} data-testid="exp-currency" onChange={(e) => changeCurrency(e.target.value)}>
              {(CURRENCIES.includes(currency) ? CURRENCIES : [currency, ...CURRENCIES]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground max-w-xs pb-2">Changing currency relabels your amounts; it does not convert them.</p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-3 rounded-xl border bg-primary/5"><p className="text-xs text-muted-foreground">Costs</p><p className="text-lg font-bold text-primary" data-testid="exp-cost">{money(sum.cost)}</p></div>
          <div className="p-3 rounded-xl border bg-primary/5"><p className="text-xs text-muted-foreground">Income</p><p className="text-lg font-bold text-primary" data-testid="exp-income">{money(sum.income)}</p></div>
          <div className="p-3 rounded-xl border bg-primary/5"><p className="text-xs text-muted-foreground">Net</p><p className={`text-lg font-bold ${net < 0 ? "text-red-500" : "text-primary"}`} data-testid="exp-net">{money(net)}</p></div>
        </div>
        {sum.pledged > 0 && <p className="text-xs text-center text-muted-foreground" data-testid="exp-pledged">Pledged, not received yet: {money(sum.pledged)}. Left out of income and net until you mark it Received.</p>}
        {sum.otherCurrency > 0 && <NoticeBox className="text-sm text-center" role="alert">{sum.otherCurrency} line{sum.otherCurrency > 1 ? "s are" : " is"} in another currency and not counted in these totals.</NoticeBox>}

        <div className="flex items-center justify-center gap-3">
          <Label className="text-xs text-muted-foreground shrink-0">Budget (optional)</Label>
          <Input aria-label="Budget (optional)" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={plannedFromProject > 0 ? `${plannedFromProject} from budget lines` : "e.g. 2000"} className="w-40 text-center" />
        </div>

        {budgetNum > 0 && (() => {
          const remaining = budgetNum - sum.cost;
          const over = remaining < 0;
          return (
            <div className={`p-4 rounded-xl border text-center ${over ? "border-red-500/40 bg-red-500/5" : "border-green-500/40 bg-green-500/5"}`} data-testid="exp-budget">
              <p className="text-xs text-muted-foreground">{over ? "Over Budget By" : "Remaining Budget"}</p>
              <p className={`text-2xl font-bold ${over ? "text-red-500" : "text-green-600"}`}>{money(Math.abs(remaining))}</p>
              <p className="text-xs text-muted-foreground mt-1">{money(sum.cost)} spent of your {money(budgetNum)} budget.</p>
            </div>
          );
        })()}

        {expenses.length > 0 && (
          <div className="flex flex-wrap items-end justify-center gap-2" data-testid="exp-filter">
            <div><Label className="text-xs">Month</Label>
              <select className="border rounded px-2 py-2 bg-background text-sm block" aria-label="Month" value={month} data-testid="exp-month" onChange={(e) => pickMonth(e.target.value)}>
                <option value="">All dates</option>
                {months.map((m) => <option key={m} value={m}>{m}</option>)}
                {month && !months.includes(month) && <option value={month}>{month}</option>}
              </select>
            </div>
            <div><Label className="text-xs">From</Label><Input aria-label="From date" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setMonth(""); }} className="w-36" data-testid="exp-from" /></div>
            <div><Label className="text-xs">To</Label><Input aria-label="To date" type="date" value={to} onChange={(e) => { setTo(e.target.value); setMonth(""); }} className="w-36" data-testid="exp-to" /></div>
            {filtered && <Button variant="ghost" size="sm" onClick={() => pickMonth("") } data-testid="exp-filter-clear">Show all</Button>}
          </div>
        )}
        {filtered && <p className="text-xs text-center text-muted-foreground" data-testid="exp-filter-note">Showing {visible.length} of {expenses.length} entries. Totals and exports cover these only.</p>}

        <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
          <span className="text-muted-foreground">Categories:</span>
          {(Object.keys(SETS) as (keyof typeof SETS)[]).map((k) => (
            <button key={k} type="button" onClick={() => { setCategorySet(k); setCategory(categoriesFor(k, kind)[0]); }}
              className={`px-2 py-1 rounded-md border ${categorySet === k ? "bg-primary text-primary-foreground" : "bg-card"}`}>{k === "business" ? "Business" : k === "personal" ? "Personal" : "Event"}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
          <div><Label>Date</Label><Input aria-label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Type</Label>
            <select className="border rounded px-2 py-2 bg-background text-sm w-full" aria-label="Type" value={kind} data-testid="exp-kind" onChange={(e) => { const k = e.target.value as ExpenseKind; setKind(k); setCategory(categoriesFor(categorySet, k)[0]); }}>
              <option value="cost">Cost</option><option value="income">Income</option>
            </select>
          </div>
          {kind === "income" && (
            <div><Label>Status</Label>
              <select className="border rounded px-2 py-2 bg-background text-sm w-full" aria-label="Income status" value={status} data-testid="exp-status" onChange={(e) => setStatus(e.target.value as IncomeStatus)}>
                <option value="paid">Received</option><option value="pledged">Pledged (not received yet)</option>
              </select>
            </div>
          )}
          <div><Label>Category</Label>
            <select className="border rounded px-2 py-2 bg-background text-sm w-full" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Amount ({currency})</Label><Input type="number" inputMode="decimal" value={amount} onChange={(e) => { setAmount(e.target.value); setAmountError(null); }} aria-invalid={!!amountError} data-testid="exp-amount" />{amountError && <p role="alert" className="text-xs text-destructive mt-1" data-testid="exp-amount-error">{amountError}</p>}</div>
          <div className="col-span-2"><Label>Description</Label><Input aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" data-testid="exp-desc" /></div>
          <div><Label>{kind === "income" ? "Client" : "Vendor"}</Label><Input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Optional" /></div>
          <div><Label>Tax %</Label><Input aria-label="Tax %" type="number" inputMode="decimal" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="0" data-testid="exp-tax" /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={taxIncluded} onChange={(e) => setTaxIncluded(e.target.checked)} /> Amount already includes tax</label>
          <div className="col-span-2 text-sm">
            <Label className="text-xs">Receipt (photo or PDF, optional)</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md border bg-card cursor-pointer text-xs"><Paperclip className="h-3.5 w-3.5" /> Attach file
                <input type="file" accept="image/*,application/pdf" className="sr-only" aria-label="Attach receipt" data-testid="exp-receipt-input" onChange={(e) => { pickReceipt(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {(receipt || (editingId && !receiptCleared && receiptFiles.has(editingId))) && (
                <>
                  <span className="text-xs truncate max-w-[10rem]" data-testid="exp-receipt-name">{receipt?.name ?? expenses.find((x) => x.id === editingId)?.receipt}</span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => { setReceipt(null); setReceiptCleared(true); }} aria-label="Remove receipt"><X className="h-3.5 w-3.5" /></Button>
                </>
              )}
            </div>
          </div>
          {!editingId && (
            <div className="col-span-2 flex items-center gap-2 flex-wrap text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} data-testid="exp-repeat" /> Repeat monthly until</label>
              <Input aria-label="Repeat until" type="date" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} disabled={!repeat} className="w-36" data-testid="exp-repeat-until" />
            </div>
          )}
          <div className="col-span-2 flex gap-2 justify-end">
            <Button onClick={saveExpense} data-testid="exp-save" className="gap-1.5">{editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}{editingId ? "Save changes" : "Add"}</Button>
            {editingId && <Button onClick={resetForm} variant="outline" aria-label="Cancel editing"><X className="h-4 w-4" /></Button>}
          </div>
        </div>
        {editingId && <p className="text-xs text-primary text-center -mt-4">Editing an existing entry — save your changes or cancel above.</p>}

        {sum.byCategory.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Costs by category</Label>
            {sum.byCategory.map((r) => (
              <div key={r.category} className="flex items-center gap-2">
                <span className="text-xs w-24 shrink-0 truncate">{r.category}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${sum.cost > 0 ? (r.amount / sum.cost) * 100 : 0}%` }} /></div>
                <span className="text-xs w-20 text-right shrink-0">{money(r.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {monthRows.length > 1 && (
          <div className="space-y-1.5" data-testid="exp-by-month">
            <Label className="text-xs text-muted-foreground">By month (costs and income)</Label>
            {monthRows.map((r) => (
              <div key={r.month} className="flex items-center gap-2">
                <span className="text-xs w-16 shrink-0">{r.month}</span>
                <div className="flex-1 space-y-0.5">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden" title={`Costs ${money(r.cost)}`}><div className="h-full bg-primary" style={{ width: `${monthMax > 0 ? (r.cost / monthMax) * 100 : 0}%` }} /></div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden" title={`Income ${money(r.income)}`}><div className="h-full bg-green-600" style={{ width: `${monthMax > 0 ? (r.income / monthMax) * 100 : 0}%` }} /></div>
                </div>
                <span className="text-xs w-28 text-right shrink-0">{money(r.cost)} / {money(r.income)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2" data-testid="exp-list">
          {visible.map((x) => (
            <div key={x.id} className="flex items-center justify-between p-2.5 rounded-lg border bg-card text-sm gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="text-xs text-muted-foreground shrink-0">{x.date}</span>
                {x.category && <span className="text-xs px-2 py-0.5 rounded-full bg-muted shrink-0">{x.category}</span>}
                {x.status === "pledged" && <span className="text-xs px-2 py-0.5 rounded-full border shrink-0" data-testid="exp-pledged-badge">Pledged</span>}
                <span className="truncate text-muted-foreground">{x.description}</span>
                {receiptFiles.has(x.id) ? <ReceiptThumb file={receiptFiles.get(x.id)!} /> : x.receipt ? <span className="text-xs text-muted-foreground shrink-0" title="Receipt file name">{x.receipt}</span> : null}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className={`font-medium ${x.kind === "income" ? "text-green-600" : ""}`}>{x.kind === "income" ? (x.status === "pledged" ? "~" : "+") : ""}{x.currency === currency ? money(expenseGross(x)) : `${x.amount} ${x.currency}`}</span>
                <Button size="icon" variant="ghost" onClick={() => startEdit(x)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => removeExpense(x.id)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
          {expenses.length === 0 && <p className="text-sm text-center text-muted-foreground py-8">Nothing logged yet.</p>}
          {expenses.length > 0 && visible.length === 0 && <p className="text-sm text-center text-muted-foreground py-8">No entries in this date range.</p>}
        </div>

        {visible.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="exp-export"><Download className="h-4 w-4" /> Export as CSV</Button>
            <Button variant="outline" onClick={exportXlsx} className="gap-2" data-testid="exp-export-xlsx"><Download className="h-4 w-4" /> Export as Excel</Button>
            <Button variant="outline" onClick={exportPdf} className="gap-2" data-testid="exp-export-pdf"><Download className="h-4 w-4" /> Export as PDF</Button>
            {receiptCount > 0 && <Button variant="outline" onClick={exportZip} className="gap-2" data-testid="exp-export-zip"><Download className="h-4 w-4" /> CSV and receipts (ZIP)</Button>}
          </div>
        )}

        {keep.on && sinceBackup >= BACKUP_REMINDER_EVERY && (
          <NoticeBox className="text-sm text-center" role="status" testId="keep-backup-reminder">
            You have added {sinceBackup} entries since your last backup. Entries kept in this browser can be lost if you clear site data, so save a backup file (Save project below, or an export).
          </NoticeBox>
        )}
        <DeviceKeepPanel keep={keep} locale={project.locale} kept="your entries, filters and settings (currency, budget, view)" notKept="Receipt files are not kept: attach them again after you reopen, and they are never uploaded." />

        <p className="text-xs text-muted-foreground text-center">
          {keep.on
            ? "Your entries are saved in this browser only and are never uploaded. Save the project file or export a backup now and then."
            : "Your entries and receipts are kept for this visit only and are not uploaded anywhere. They are erased when you close the tab, so save the project file or export if you want to keep them."}{" "}
          {expenses.length > 0 && <button type="button" className="underline" onClick={clearAll}>Delete all my data</button>}
        </p>
      </div>
      <ProjectBar passKeyNote={false} neutralName="My expenses" onSaved={() => markBackedUp()} onOpened={() => markBackedUp()} />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
